import moment from "moment/moment";
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'moment/locale/tr';
import { normalizeSearch } from "./utils/text-search";

const phoneFormat = (phoneNumber) => {
    if (!phoneNumber)
        return phoneNumber;

    if (phoneNumber.length > 15)
        return phoneNumber.substring(0, 15)

    const match = phoneNumber
        .replace(/\D+/g, '').replace(/^1/, '')
        .match(/([^\d]*\d[^\d]*){1,10}$/)[0]

    const part1 = match.length > 2 ? `(${match.substring(0, 3)})` : match
    const part2 = match.length > 3 ? ` ${match.substring(3, 6)}` : ''
    const part3 = match.length > 6 ? ` ${match.substring(6, 8)}` : ''
    const part4 = match.length > 8 ? ` ${match.substring(8, 10)}` : ''
    return `${part1}${part2}${part3}${part4}`
}

const intlPhoneFormat = (code, phoneNumber) => {
    const num = phoneFormat(phoneNumber);
    return `+${code} ${num}`
}

const clearPhoneFormat = (phoneNumber) => {
    return phoneNumber
        .replaceAll(')', '')
        .replaceAll('(', '')
        .replaceAll(' ', '')
        .replaceAll('-', '')
        .trim();
}

const getUrlParam = (search, key) => {
    const queryParams = new URLSearchParams(search);
    return queryParams.get(key);
}

const getUrlParamAsObject = (search) => {
    const queryParams = new URLSearchParams(search);
    return JSON.parse(queryParams.get('filter') ?? '{}');
}

function paramsToObject(entries) {
    const result = {}
    for (const [key, value] of entries) { // each 'entry' is a [key, value] tupple
        result[key] = value;
    }
    return result;
}

function saveFile({ blobLink, fileName }) {
    const link = document.createElement('a');
    link.href = blobLink;
    link.setAttribute('download', fileName || 'file'); //or any other extension
    document.body.appendChild(link);
    link.click();
}

function getFile({ accept }) {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.id = 'file';
        input.accept = accept

        input.onchange = async (e) => {
            const file = input.files[0];
            resolve({
                data: file,
                fileName: file.name
            });
        }

        input.click()
    })
}

function getFileAsBase64({ accept }) {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.id = 'file';
        input.accept = accept

        input.onchange = async (e) => {
            const file = input.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve({
                data: reader.result.replace(new RegExp('data:.*/.*;base64,'), ''),
                url: reader.result,
                fileName: file.name
            });
            reader.onerror = error => reject(error);
        }

        input.click()
    })
}

const DATE_OPTIONS = {
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    LAST_2_DAYS: 'Last 2 Days',
    THIS_WEEK: 'This Week',
    THIS_MONTH: 'This Month',
    LAST_7_DAYS: 'Last 7 Days',
    LAST_15_DAYS: 'Last 15 Days',
    LAST_30_DAYS: 'Last 30 Days',
    LAST_3_MONTH: 'Last 3 Month',
    LAST_6_MONTH: 'Last 6 Month',
}

const beginOfDay = (date) => {
    return date?.clone().startOf('day').utc().format('yyyy-MM-DDTHH:mm:ss+00:00');
}

const endOfDay = (date) => {
    return date?.clone().endOf('day').utc().format('yyyy-MM-DDTHH:mm:ss+00:00');
}

const dateFormat = (dateStr, format) => {
    if (!dateStr)
        return '-';

    return moment(dateStr).format(format || 'DD/MM/yyyy HH:mm')
}


const getDateFromNow = (option) => {
    let now = moment();
    let start = now.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    let end = moment();
    end.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })

    if (option === DATE_OPTIONS.TODAY) {
        start = now.set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    } else if (option === DATE_OPTIONS.YESTERDAY) {
        start = now.subtract(1, 'days');
        end = start
    } else if (option === DATE_OPTIONS.LAST_2_DAYS) {
        start = now.subtract(2, 'days');
    } else if (option === DATE_OPTIONS.THIS_WEEK) {
        start = now.startOf('isoWeek');
    } else if (option === DATE_OPTIONS.THIS_MONTH) {
        start = now.startOf('month')
    } else if (option === DATE_OPTIONS.LAST_7_DAYS) {
        start = now.subtract(7, 'days')
    } else if (option === DATE_OPTIONS.LAST_15_DAYS) {
        start = now.subtract(15, 'days')
    } else if (option === DATE_OPTIONS.LAST_30_DAYS) {
        start = now.subtract(30, 'days')
    } else if (option === DATE_OPTIONS.LAST_3_MONTH) {
        start = now.subtract(3, 'months')
    } else if (option === DATE_OPTIONS.LAST_6_MONTH) {
        start = now.subtract(6, 'months')
    }

    return {
        start,
        end
    }
}

const getAge = (dateStr) => {
    if (!dateStr)
        return '*';
    else if (dateStr.includes('0001-01-01'))
        return '*'

    return moment().diff(dateStr, 'years');
}

const CurrencyFormat = (data, options) => {
    const formatter = new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        ...(options || {})
    })

    return formatter.format(data).replace('TRY', '₺ .')
}

const updateUrlParam = (filter) => {
    const params = {
        filter: JSON.stringify(filter)
    }

    const queryParams = new URLSearchParams(params).toString();

    if (window.history.pushState) {
        const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + `?${queryParams}`;
        window.history.pushState({ path: newurl }, '', newurl);
    }
}

const getLocale = () => {
    return locale
}

const isBeforeToday = (dateStr) => {
    if (!dateStr) return false;
    const date = moment(dateStr).startOf('day');
    const today = moment().startOf('day');
    return date.isBefore(today);
}

const getToday = () => {
    return moment();
}

const getTodayStart = () => {
    return moment().startOf('day');
}

const isToday = (dateStr) => {
    if (!dateStr) return false;
    const date = moment(dateStr);
    const today = moment();
    return date.isSame(today, 'day');
}

const normalizeText = (text) => {
    // Ortak normalize (core/utils/text-search): Türkçe harfler ASCII'ye, kalan aksanlar NFD ile atılır.
    // Çıktı BÜYÜK harf — mevcut çağrı yerleri normalizeText(a).includes(normalizeText(b)) yapıyor.
    return normalizeSearch(text).toUpperCase();
};

const validateEmail = (email) => {
    if (!email) return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
};

const Helper = {
    Text: {
        normalizeText,
        validateEmail
    },
    Phone: {
        phoneFormat,
        intlPhoneFormat,
        clearPhoneFormat
    },
    File: {
        saveFile,
        getFileAsBase64,
        getFile
    },
    Date: {
        DATE_OPTIONS,
        beginOfDay,
        endOfDay,
        getAge,
        dateFormat,
        getDateFromNow,
        getLocale,
        isToday,
        isBeforeToday,  // burası önemli
        getToday,
        getTodayStart
    },
    UrlParams: {
        getUrlParam,
        updateUrlParam,
        getUrlParamAsObject
    },
    Currency: {
        format: CurrencyFormat
    }
}

export default Helper;
